import React, { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataProvider, useData } from "./context/DataContext";
import { userMatchesField } from "./utils/helpers";

import LoginPage from "./pages/LoginPage";
import Sidebar   from "./components/Sidebar";

import AdminDashboard  from "./pages/admin/AdminDashboard";
import AllTasksAdmin   from "./pages/admin/AllTasksAdmin";
import PendingTasksAdmin from "./pages/admin/PendingTasksAdmin";
import CompletedTasksAdmin from "./pages/admin/CompletedTasksAdmin";
import OverdueTasksAdmin from "./pages/admin/OverdueTasksAdmin";
import RecurringTasksAdmin from "./pages/admin/RecurringTasksAdmin";
import MonitorAdmin    from "./pages/admin/MonitorAdmin";
import UsersPage       from "./pages/admin/UsersPage";
import GenerateReportPage from "./pages/admin/GenerateReportPage";
import CreateTaskPage  from "./pages/admin/CreateTaskPage";

import EmployeeDashboard  from "./pages/employee/EmployeeDashboard";
import AllTasksEmployee   from "./pages/employee/AllTasksEmployee";
import PendingTasks       from "./pages/employee/PendingTasks";
import CompletedTasks     from "./pages/employee/CompletedTasks";
import OverdueTasks       from "./pages/employee/OverdueTasks";
import CreatedTasks       from "./pages/employee/CreatedTasks";
import MonitorEmployee    from "./pages/employee/MonitorEmployee";
import CreateTaskEmployee from "./pages/employee/CreateTaskEmployee";

const PAGE_LABELS = {
  dashboard: "Dashboard", "all-tasks": "All Tasks",
  monitor: "Monitor", "create-task": "Create Task",
  users: "Employees", pending: "Pending Tasks",
  completed: "Completed Tasks", overdue: "Overdue Tasks",
  created: "Created Tasks",
  recurring: "Recurring Tasks",
  report: "Generate Report",
};

// Notification timestamp format:
//   < 1 min  → "just now"
//   < 1 hour → "Xm ago"
//   < 1 day  → clock time only ("2:30 PM")
//   < 1 week → weekday + time ("Mon 2:30 PM")
//   older    → date + time ("23 May 2:30 PM")
function formatRelativeTime(ms) {
  if (!ms) return "";
  const date = new Date(Number(ms));
  const diff = Date.now() - Number(ms);
  if (diff < 0) return "just now";
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;

  let timeStr = "";
  try { timeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { timeStr = ""; }

  if (diff < 86_400_000) return timeStr;

  if (diff < 7 * 86_400_000) {
    let day = "";
    try { day = date.toLocaleDateString([], { weekday: "short" }); } catch {}
    return day ? `${day} ${timeStr}` : timeStr;
  }

  let dateStr = "";
  try { dateStr = date.toLocaleDateString([], { day: "numeric", month: "short" }); } catch {}
  return dateStr ? `${dateStr} ${timeStr}` : timeStr;
}

function NotificationBell({ onNavigate }) {
  const { commentCounts, commentLatestTimes, readComments, commentSeenAt, adminRecords, userRecords } = useData();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Read notifications stay in the dropdown for 24 hours after they were
  // marked as seen; after that they auto-disappear. Unread items never expire.
  const SEEN_RETENTION_MS = 24 * 60 * 60 * 1000;

  // Build notification list: { taskId, taskName, unread, total }
  // - Admins see every task that has any comments.
  // - Other users see only tasks they're involved in (assigned / creator / supervisor).
  // - Unread items: always shown, bold.
  // - Read items: shown normalized for up to 24h after being seen, then dropped.
  const notificationList = React.useMemo(() => {
    const myTaskIds = new Set();
    if (!isAdmin) {
      for (const r of (userRecords || [])) {
        if (r.user_id && r.user_id === user?.userId) {
          const tid = r.fields?.["Task ID"];
          if (tid) myTaskIds.add(String(tid));
        }
      }
      for (const r of (adminRecords || [])) {
        const f = r.fields || {};
        const tid = f["Task ID"];
        if (tid && (
          userMatchesField(f["Task Creator"], user) ||
          userMatchesField(f["Supervisor"], user) ||
          userMatchesField(f["Assigned By"], user) ||
          userMatchesField(f["Assigned Users"], user)
        )) {
          myTaskIds.add(String(tid));
        }
      }
    }

    // Build a set of currently-existing task IDs. Notifications referring
    // to tasks that have since been deleted will be skipped, so the bell
    // doesn't keep showing comment counts for tasks that no longer exist.
    const existingTaskIds = new Set();
    const taskNameById = {};
    for (const r of (adminRecords || [])) {
      const tid = r.fields?.["Task ID"];
      const tname = r.fields?.["Task Name"];
      if (tid) {
        const key = String(tid);
        existingTaskIds.add(key);
        taskNameById[key] = tname || "Untitled";
      }
    }

    const now = Date.now();
    const items = [];
    for (const [taskId, total] of Object.entries(commentCounts || {})) {
      if (!total || total <= 0) continue;
      // Skip notifications for tasks that no longer exist (deleted).
      if (!existingTaskIds.has(String(taskId))) continue;
      if (!isAdmin && !myTaskIds.has(String(taskId))) continue;
      const read = readComments?.[taskId] || 0;
      const unread = Math.max(0, total - read);
      // If user already read everything, only keep the notification visible
      // for SEEN_RETENTION_MS after they last marked it read.
      if (unread === 0) {
        const seenAt = commentSeenAt?.[taskId];
        if (!seenAt || (now - seenAt) > SEEN_RETENTION_MS) continue;
      }
      items.push({
        taskId,
        taskName: taskNameById[taskId] || taskId,
        unread,
        total,
        latestTime: commentLatestTimes?.[taskId] || 0,
      });
    }
    // Sort strictly by latest-message time (newest first). Read/unread state
    // does NOT affect position — a notification stays in the same place once
    // seen, it just visually normalizes from bold to regular weight.
    return items.sort((a, b) => (b.latestTime || 0) - (a.latestTime || 0));
  }, [commentCounts, commentLatestTimes, readComments, commentSeenAt, adminRecords, userRecords, user, isAdmin]);

  const totalUnread = notificationList.reduce((s, x) => s + x.unread, 0);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handlePick = (taskId) => {
    setOpen(false);

    // Try All Tasks first; if the task isn't found there, fall back to Monitor.
    if (typeof onNavigate === "function") onNavigate("all-tasks");

    const fallbackHandler = (e) => {
      const missedId = String(e.detail?.taskId || "");
      if (missedId !== String(taskId)) return;
      window.removeEventListener("tms:task-not-found", fallbackHandler);
      // Task not on All Tasks — switch to Monitor
      if (typeof onNavigate === "function") onNavigate("monitor");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("tms:open-task-comments", { detail: { taskId } }));
      }, 250);
    };
    window.addEventListener("tms:task-not-found", fallbackHandler);

    // Dispatch to All Tasks page after it mounts
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("tms:open-task-comments", { detail: { taskId } }));
    }, 250);

    // Cleanup fallback listener after 2s in case it never fires
    setTimeout(() => {
      window.removeEventListener("tms:task-not-found", fallbackHandler);
    }, 2000);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={totalUnread > 0 ? `${totalUnread} unread notifications` : "Notifications"}
        title={totalUnread > 0 ? `${totalUnread} unread comment${totalUnread === 1 ? "" : "s"}` : "Notifications"}
        style={{
          position: "relative",
          width: 36, height: 36, borderRadius: "50%",
          border: "1px solid var(--border-default)",
          background: "var(--surface-2)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, padding: 0,
          animation: totalUnread > 0 ? "notifBellPulse 1.6s ease-in-out infinite" : "none",
          transition: "var(--transition-fast)",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "var(--surface-3)"}
        onMouseLeave={e => e.currentTarget.style.background = "var(--surface-2)"}
      >
        🔔
        {totalUnread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            minWidth: 18, height: 18, padding: "0 5px",
            background: "var(--danger)", color: "white",
            fontSize: 10, fontWeight: 800,
            borderRadius: "var(--radius-pill)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "var(--shadow-glow-danger)",
            border: "2px solid var(--surface-0)",
          }}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: "min(320px, calc(100vw - 24px))",
          maxHeight: "min(420px, 70vh)",
          background: "var(--surface-1)", borderRadius: 12,
          border: "1px solid var(--border-default)",
          boxShadow: "var(--shadow-xl)",
          backdropFilter: "blur(var(--blur-md))",
          zIndex: 1000, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            padding: "12px 14px", borderBottom: "1px solid var(--border-subtle)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Notifications</span>
            {totalUnread > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--primary)" }}>
                {totalUnread} unread
              </span>
            )}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {notificationList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--text-disabled)" }}>
                <div style={{ fontSize: 28, marginBottom: 6, opacity: 0.5 }}>🔕</div>
                <div style={{ fontSize: 12 }}>No notifications.</div>
              </div>
            ) : (
              notificationList.map(item => {
                const isUnread = item.unread > 0;
                return (
                  <button
                    key={item.taskId}
                    type="button"
                    onClick={() => handlePick(item.taskId)}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "10px 14px", border: "none",
                      background: isUnread ? "var(--primary-light)" : "transparent",
                      borderBottom: "1px solid var(--border-default)",
                      cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: 10,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
                    onMouseLeave={e => e.currentTarget.style.background = isUnread ? "var(--primary-light)" : "transparent"}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: isUnread ? "var(--primary-muted)" : "var(--surface-3)",
                      color: isUnread ? "var(--primary)" : "var(--text-tertiary)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, flexShrink: 0,
                      position: "relative",
                    }}>
                      💬
                      {isUnread && (
                        <span style={{
                          position: "absolute", top: -2, right: -2,
                          width: 8, height: 8, borderRadius: "50%",
                          background: "var(--primary)",
                          border: "2px solid var(--surface-1)",
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
                        marginBottom: 2,
                      }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700,
                          color: isUnread ? "var(--primary)" : "var(--text-tertiary)",
                          letterSpacing: "0.04em",
                        }}>{item.taskId}</span>
                        {item.latestTime > 0 && (
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            color: isUnread ? "var(--primary)" : "var(--text-tertiary)",
                            whiteSpace: "nowrap",
                          }}
                          title={new Date(item.latestTime).toLocaleString()}>
                            {formatRelativeTime(item.latestTime)}
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: 13,
                        fontWeight: isUnread ? 800 : 500,
                        color: isUnread ? "var(--text-primary)" : "var(--text-secondary)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{item.taskName}</div>
                    </div>
                    {isUnread ? (
                      <span style={{
                        minWidth: 22, height: 22, padding: "0 7px",
                        background: "var(--danger)", color: "white",
                        fontSize: 11, fontWeight: 800, borderRadius: 11,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>{item.unread}</span>
                    ) : (
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: "var(--text-disabled)",
                        flexShrink: 0,
                      }}>{item.total}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TopBar({ activePage, onRefresh, onMenuClick, onNavigate }) {
  const { user, logout }          = useAuth();
  const { loading, lastFetched }  = useData();

  return (
    <div className="topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button className="hamburger" onClick={onMenuClick} aria-label="Toggle sidebar">☰</button>
        <span style={{ fontSize: 13, color: "var(--text-disabled)", textTransform: "capitalize" }}>{user?.role}</span>
        <span style={{ color: "var(--border-default)" }}>/</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
          {PAGE_LABELS[activePage] || activePage}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {lastFetched && (
          <span style={{ fontSize: 11, color: "var(--text-disabled)" }}>Updated {lastFetched}</span>
        )}

        <NotificationBell onNavigate={onNavigate} />

        <div style={{
          width: 34, height: 34, borderRadius: "50%",
          background: user?.role === "admin" ? "var(--primary)" : "var(--success)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--surface-0)", fontSize: 12, fontWeight: 700,
          overflow: "hidden",
          border: "1px solid var(--border-subtle)",
        }}>
          {user?.avatar
            ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : (user?.initials || user?.name?.[0]?.toUpperCase())
          }
        </div>
      </div>
    </div>
  );
}

function AppShell() {
  const { user, loading: authLoading } = useAuth();
  const { refresh, loading }           = useData();
  const [activePage, setActivePage]    = useState("dashboard");
  const [sidebarOpen, setSidebarOpen]  = useState(false);
  const [adminViewMode, setAdminViewMode] = useState(() => localStorage.getItem("tms_admin_view_mode") || "admin");

  useEffect(() => {
    localStorage.setItem("tms_admin_view_mode", adminViewMode);
  }, [adminViewMode]);

  useEffect(() => { if (user) refresh(); }, [user]);

  const handleToggleViewMode = () => {
    setAdminViewMode(p => {
      const next = p === "admin" ? "employee" : "admin";
      setActivePage("dashboard");
      return next;
    });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <div className="spinner" style={{ margin: "0 auto 12px" }} />
          <p style={{ color: "var(--text-disabled)", fontSize: 14 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const isAdmin = user.role === "admin";

  const renderPage = () => {
    if (isAdmin && adminViewMode === "admin") {
      switch (activePage) {
        case "dashboard":    return <AdminDashboard onNavigate={setActivePage} />;
        case "all-tasks":   return <AllTasksAdmin />;
        case "pending":     return <PendingTasksAdmin />;
        case "completed":   return <CompletedTasksAdmin />;
        case "overdue":     return <OverdueTasksAdmin />;
        case "recurring":   return <RecurringTasksAdmin />;
        case "monitor":     return <MonitorAdmin />;
        case "users":       return <UsersPage />;
        case "report":      return <GenerateReportPage />;
        case "create-task": return <CreateTaskPage />;
        default:            return <AdminDashboard onNavigate={setActivePage} />;
      }
    } else {
      switch (activePage) {
        case "dashboard":   return <EmployeeDashboard onNavigate={setActivePage} />;
        case "all-tasks":   return <AllTasksEmployee />;
        case "pending":     return <PendingTasks />;
        case "completed":   return <CompletedTasks />;
        case "overdue":     return <OverdueTasks />;
        case "created":     return <CreatedTasks />;
        case "monitor":     return <MonitorEmployee />;
        case "create-task": return <CreateTaskEmployee />;
        default:            return <EmployeeDashboard onNavigate={setActivePage} />;
      }
    }
  };

  return (
    <div className="layout">
      <Sidebar 
        activePage={activePage} 
        onNavigate={setActivePage} 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        adminViewMode={adminViewMode}
        onToggleViewMode={handleToggleViewMode}
      />
      <div className="main-content">
        <TopBar activePage={activePage} onRefresh={refresh} onMenuClick={() => setSidebarOpen(o => !o)} onNavigate={setActivePage} />
        <div className="page-content">{renderPage()}</div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <AppShell />
      </DataProvider>
    </AuthProvider>
  );
}
